import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { Upload, FileText, X, Loader2, AlertCircle } from 'lucide-react';
import { quotesAPI } from '../../lib/api';

export default function PdfUploadDialog({
  open,
  onOpenChange,
  onExtracted
}) {
  const [file, setFile] = useState(null);
  const [useOcr, setUseOcr] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.includes('pdf')) {
        setError('Veuillez selectionner un fichier PDF');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('Le fichier ne doit pas depasser 10 Mo');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setExtractedData(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.type.includes('pdf')) {
        setError('Veuillez selectionner un fichier PDF');
        return;
      }
      if (droppedFile.size > 10 * 1024 * 1024) {
        setError('Le fichier ne doit pas depasser 10 Mo');
        return;
      }
      setFile(droppedFile);
      setError(null);
      setExtractedData(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleExtract = async () => {
    if (!file) return;

    setExtracting(true);
    setError(null);

    try {
      const response = await quotesAPI.extractPdf(file, useOcr);
      setExtractedData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'extraction");
    } finally {
      setExtracting(false);
    }
  };

  const handleConfirm = () => {
    if (extractedData) {
      onExtracted(extractedData, file);
    }
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setExtractedData(null);
    setError(null);
    setUseOcr(false);
    onOpenChange(false);
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Importer un devis PDF</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone */}
          <div
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors dark:border-gray-600 dark:hover:border-blue-500"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className="w-8 h-8 text-primary" />
                <span className="font-medium dark:text-white">{file.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => { e.stopPropagation(); setFile(null); setExtractedData(null); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">
                  Cliquez ou glissez un fichier PDF ici
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Max 10 Mo
                </p>
              </>
            )}
          </div>

          {/* OCR Option */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="ocr"
              checked={useOcr}
              onCheckedChange={setUseOcr}
            />
            <Label htmlFor="ocr" className="text-sm cursor-pointer">
              Utiliser l'OCR (pour les documents scannes)
            </Label>
          </div>

          {/* Error display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Extracted data preview */}
          {extractedData && (
            <div className="bg-muted p-4 rounded-lg space-y-2 dark:bg-gray-800">
              <h4 className="font-medium dark:text-white">Donnees extraites :</h4>
              {extractedData.total && (
                <p className="text-sm"><span className="text-muted-foreground">Total :</span> <span className="dark:text-white">{formatCurrency(extractedData.total)}</span></p>
              )}
              {extractedData.subtotal && (
                <p className="text-sm"><span className="text-muted-foreground">Sous-total HT :</span> <span className="dark:text-white">{formatCurrency(extractedData.subtotal)}</span></p>
              )}
              {extractedData.tax_rate && (
                <p className="text-sm"><span className="text-muted-foreground">TVA :</span> <span className="dark:text-white">{extractedData.tax_rate}%</span></p>
              )}
              {extractedData.items?.length > 0 && (
                <p className="text-sm"><span className="text-muted-foreground">Lignes detectees :</span> <span className="dark:text-white">{extractedData.items.length}</span></p>
              )}
              {extractedData.valid_until && (
                <p className="text-sm"><span className="text-muted-foreground">Validite :</span> <span className="dark:text-white">{extractedData.valid_until}</span></p>
              )}
              {extractedData.company && (
                <p className="text-sm"><span className="text-muted-foreground">Entreprise :</span> <span className="dark:text-white">{extractedData.company}</span></p>
              )}
              {extractedData.raw_text && (
                <details className="mt-2">
                  <summary className="text-sm text-muted-foreground cursor-pointer">Voir le texte brut</summary>
                  <pre className="mt-2 text-xs bg-background p-2 rounded max-h-32 overflow-auto dark:bg-gray-900 dark:text-gray-300">
                    {extractedData.raw_text.slice(0, 1000)}
                    {extractedData.raw_text.length > 1000 && '...'}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          {!extractedData ? (
            <Button onClick={handleExtract} disabled={!file || extracting}>
              {extracting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Extraire les donnees
            </Button>
          ) : (
            <Button onClick={handleConfirm}>
              Pre-remplir le formulaire
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
